import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { IHelpExtensionHandler, VALIDATION_SERVICE, WeatherService, WeatherConfig, WdWeatherConfigModalComponent } from '@luzula/luzula-lib';
import { MoorsubstratInfoModalComponent } from './moorsubstrat-info-modal.component';
import { TorfMuddeArtenInfoModalComponent } from './torf-muddearten-info-modal.component';
import { ZersetzungsgradInfoModalComponent } from './zersetzungsgrad-info-modal.component';
import { EntwaesserungInfoModalComponent } from './entwaesserung-info-modal.component';
import { WasserformInfoModalComponent } from './wasserform-info-modal.component';
import { ReliefInfoModalComponent } from './relief-info-modal.component';
import { HorizontfolgetypenInfoModalComponent } from './horizontfolgetypen-info-modal.component';
import { OekologischerMoortypInfoModalComponent } from './oekologischer-moortyp-info-modal.component';
import { HydrogenetischerMoortypInfoModalComponent } from './hydrogenetischer-moortyp-info-modal.component';
import { FbfPraefixInfoModalComponent } from './fbf-praefix-info-modal.component';
import { HumusgehaltInfoModalComponent } from './humusgehalt-info-modal.component';
import { LagerungsdichteInfoModalComponent } from './lagerungsdichte-info-modal.component';
import { SkelettgehaltInfoModalComponent } from './skelettgehalt-info-modal.component';
import { KarbonatgehaltInfoModalComponent } from './karbonatgehalt-info-modal.component';
import { AuthService } from 'src/app/auth.service';
import { AufnahmePunktDocument } from 'src/app/data-model';

@Injectable()
export class BohrpunktHelpExtensionHandler implements IHelpExtensionHandler {
	private authService = inject(AuthService);
	private weatherService = inject(WeatherService);
	private vs = inject(VALIDATION_SERVICE);

	private static readonly RELIEF_TOPICS = new Set([
		'Neigungsstufe', 'Woelbung', 'Hangrichtung', 'ReliefformUmgebung',
		'Hanganschluss', 'Hangteil', 'Hoehenspanne', 'InnereRauhheit'
	]);

	canHandle(name: string): boolean {
		return name === 'Witterung' || name === 'Moorsubstrat' || name === 'TorfMuddeArten' || name === 'Zersetzungsgrad' || name === 'Humusgehalt' || name === 'Lagerungsdichte' || name === 'Skelettgehalt' || name === 'Karbonat' || name === 'Entwässerungsverhalten' || name === 'Wasserform' || name === 'Wasserform2' || name === 'Horizontfolgetypen' || name === 'OekologischerMoortyp' || name === 'HydrogenetischerMoortyp' || name === 'FBFPrefix' || name.startsWith('FBFPrefix[') || BohrpunktHelpExtensionHandler.RELIEF_TOPICS.has(name);
	}

	openHelp(name: string, context: Record<string, any>, modalService: NgbModal): Promise<any> | void {
		if (name === 'Witterung') {
			const bohrpunkt = context['bohrpunkt'] as AufnahmePunktDocument | undefined;
			const modalRef = modalService.open(WdWeatherConfigModalComponent, { centered: true, size: 'lg' });
			modalRef.componentInstance.canEdit = this.authService.canIbe('manager') || this.authService.canIbe('entwickler');
			modalRef.componentInstance.hasRawData = !!bohrpunkt?.extendedHeader?.witterungRohdaten;
			modalRef.componentInstance.rawData = bohrpunkt?.extendedHeader?.witterungRohdaten;
			modalRef.componentInstance.reinterpretFn = (rohdaten: any, _config: WeatherConfig) => {
				return this.weatherService.reinterpretRohdaten(rohdaten).full;
			};
			modalRef.componentInstance.reinterpreted.subscribe((result: string) => {
				if (bohrpunkt?.extendedHeader) {
					bohrpunkt.extendedHeader.witterung = result;
					// Schreibt am Eingabefeld vorbei: Ohne die Marke „geaendert" erschien
					// kein Speichern-Knopf, die neue Fassung ging still verloren
					// (Nutzer-Befund 15.09.2026, wie an den Wetter-Knoepfen).
					this.vs.dirty.next(true);
				}
			});
			return;
		}

		if (name === 'Moorsubstrat') {
			modalService.open(MoorsubstratInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'TorfMuddeArten') {
			modalService.open(TorfMuddeArtenInfoModalComponent, { centered: true, size: 'xl', scrollable: true });
			return;
		}

		if (name === 'Zersetzungsgrad') {
			modalService.open(ZersetzungsgradInfoModalComponent, { centered: true, size: 'xl', scrollable: true });
			return;
		}

		if (name === 'Humusgehalt') {
			modalService.open(HumusgehaltInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'Lagerungsdichte') {
			// 'lg': Die Wirkungstabelle hat drei Zahlenspalten plus die Bodenarten;
			// im Standardfenster musste man waagerecht scrollen.
			modalService.open(LagerungsdichteInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'Skelettgehalt') {
			modalService.open(SkelettgehaltInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'Karbonat') {
			modalService.open(KarbonatgehaltInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'Entwässerungsverhalten') {
			modalService.open(EntwaesserungInfoModalComponent, { centered: true, size: 'lg', scrollable: true });
			return;
		}

		if (name === 'Horizontfolgetypen') {
			modalService.open(HorizontfolgetypenInfoModalComponent, { centered: true, scrollable: true });
			return;
		}

		if (name === 'OekologischerMoortyp') {
			modalService.open(OekologischerMoortypInfoModalComponent, { centered: true, size: 'xl', scrollable: true });
			return;
		}

		if (name === 'HydrogenetischerMoortyp') {
			modalService.open(HydrogenetischerMoortypInfoModalComponent, { centered: true, scrollable: true });
			return;
		}

		if (name === 'FBFPrefix' || name.startsWith('FBFPrefix[')) {
			// Der Name kommt aus der Oberflaeche als "FBFPrefix[i]" (Praefix je
			// Bodenglied), deshalb hier die Praefix-Pruefung statt eines Vergleichs.
			modalService.open(FbfPraefixInfoModalComponent, { centered: true, scrollable: true });
			return;
		}

		if (name === 'Wasserform' || name === 'Wasserform2') {
			const modalRef = modalService.open(WasserformInfoModalComponent, { centered: true, size: 'xl', scrollable: true });
			modalRef.componentInstance.activeTab = name === 'Wasserform2' ? 'stau' : 'grund';
			return;
		}

		if (BohrpunktHelpExtensionHandler.RELIEF_TOPICS.has(name)) {
			// 'lg': Die Tabellen haben bis zu vier Spalten plus Bild. Im
			// Standardfenster musste man waagerecht scrollen, und die Bildspalte
			// stand ausserhalb (Nutzer-Befund 10.09.2026).
			const modalRef = modalService.open(ReliefInfoModalComponent, { centered: true, scrollable: true, size: 'lg' });
			modalRef.componentInstance.topicName = name;
			modalRef.componentInstance.aktuellerWert = context['currentValue'];
			// Wo das Feld gerade nicht gilt, sagt die Oberflaeche das ueber den
			// Kontext (Hangrichtung bei Ebenen). Ohne Angabe darf uebernommen
			// werden - das Fenster entscheidet dann an der Tabelle selbst.
			modalRef.componentInstance.uebernahmeMoeglich = context['uebernahme'] !== false;
			// Die Tabellen mit Listenwerten geben den angetippten Wert zurueck;
			// wird nur gelesen und weggeklickt, lehnt das Versprechen ab und die
			// Hilfe-Komponente der Bibliothek verschluckt das.
			return modalRef.result;
		}

		// Hauptbodenform und Feinbodenform laufen nicht mehr ueber das (i): Katalog
		// und Auswahlliste sind Knoepfe im Feld (BodenformNachschlagenService,
		// Nutzer-Entscheidung 28.09.2026).
	}
}
